/**
 * Deterministic SDK chrome strings — NOT translated by IndicTrans2.
 * Missing keys fall back to English.
 */

export type ConsentUiStringKey =
  | "acceptAll"
  | "rejectAll"
  | "preferences"
  | "savePreferences"
  | "revokeAll"
  | "manageConsent"
  | "withdrawConsent"
  | "privacyPolicy"
  | "cookiePolicy"
  | "learnMore"
  | "confirm"
  | "cancel"
  | "close"
  | "language"
  | "weUseCookies"
  | "grievanceOfficer"
  | "dataRightsRequest"
  | "dataRightsRequestHint"
  | "alwaysActive"
  | "securedBy"
  | "yourIdOptional"
  | "cookiesUsed"
  | "terms"
  | "contact"
  | "dpo"
  | "childrenWarningTitle"
  | "childrenWarningDefault"
  | "ageGateLabel"
  | "ageGateMinorNotice";

const EN: Record<ConsentUiStringKey, string> = {
  acceptAll: "Accept All",
  rejectAll: "Reject All",
  preferences: "Preferences",
  savePreferences: "Save preferences",
  revokeAll: "Revoke All",
  manageConsent: "Manage Consent",
  withdrawConsent: "Withdraw Consent",
  privacyPolicy: "Privacy Policy",
  cookiePolicy: "Cookie Policy",
  learnMore: "Learn More",
  confirm: "Confirm",
  cancel: "Cancel",
  close: "Close",
  language: "Language",
  weUseCookies: "We use cookies",
  grievanceOfficer: "Grievance Officer",
  dataRightsRequest: "Submit a Data Rights Request",
  dataRightsRequestHint: "Access, correct, or delete your data, or file a grievance",
  alwaysActive: "Always Active",
  securedBy: "Secured by",
  yourIdOptional: "Your ID (optional)",
  cookiesUsed: "Cookies",
  terms: "Terms",
  contact: "Contact",
  dpo: "Data Protection Officer",
  childrenWarningTitle: "Notice for Minors & Children (DPDP Act Section 9)",
  childrenWarningDefault:
    "This website is intended for users 18 years and above. Under Section 9 of the DPDP Act 2023, personal data of children requires verifiable parental or lawful guardian consent. Tracking and targeted advertising are strictly prohibited for minors.",
  ageGateLabel: "I confirm that I am 18 years of age or older",
  ageGateMinorNotice:
    "Notice: Since you are under 18, marketing, analytics and tracking cookies are automatically disabled per DPDP Act Section 9(3).",
};

type PartialUi = Partial<Record<ConsentUiStringKey, string>>;

/** Curated static labels for Eighth Schedule languages (fallback → EN). */
const BY_LANG: Record<string, PartialUi> = {
  en: EN,
  hi: {
    acceptAll: "सभी स्वीकार करें",
    rejectAll: "सभी अस्वीकार करें",
    preferences: "प्राथमिकताएँ",
    savePreferences: "प्राथमिकताएँ सहेजें",
    revokeAll: "सभी रद्द करें",
    manageConsent: "सहमति प्रबंधित करें",
    withdrawConsent: "सहमति वापस लें",
    privacyPolicy: "गोपनीयता नीति",
    cookiePolicy: "कुकी नीति",
    learnMore: "और जानें",
    confirm: "पुष्टि करें",
    cancel: "रद्द करें",
    close: "बंद करें",
    language: "भाषा",
    weUseCookies: "हम कुकीज़ का उपयोग करते हैं",
    grievanceOfficer: "शिकायत अधिकारी",
    dataRightsRequest: "डेटा अधिकार अनुरोध जमा करें",
    dataRightsRequestHint: "अपने डेटा तक पहुँचें, सही करें या हटाएँ, या शिकायत दर्ज करें",
    alwaysActive: "हमेशा सक्रिय",
    securedBy: "द्वारा सुरक्षित",
    yourIdOptional: "आपकी ID (वैकल्पिक)",
    childrenWarningTitle: "नाबालिगों और बच्चों के लिए सूचना (DPDP धारा 9)",
    childrenWarningDefault:
      "यह वेबसाइट 18 वर्ष और उससे अधिक आयु के उपयोगकर्ताओं के लिए है। DPDP अधिनियम 2023 की धारा 9 के तहत बच्चों के डेटा के लिए माता-पिता की सहमति अनिवार्य है। नाबालिगों के लिए ट्रैकिंग और लक्षित विज्ञापन प्रतिबंधित हैं।",
    ageGateLabel: "मैं पुष्टि करता/करती हूँ कि मेरी आयु 18 वर्ष या उससे अधिक है",
    ageGateMinorNotice:
      "चूंकि आपकी आयु 18 वर्ष से कम है, DPDP धारा 9(3) के अनुसार ट्रैकिंग और विज्ञापन कुकीज़ अक्षम कर दी गई हैं।",
  },
  gu: {
    acceptAll: "બધું સ્વીકારો",
    rejectAll: "બધું નકારો",
    preferences: "પસંદગીઓ",
    savePreferences: "પસંદગીઓ સાચવો",
    revokeAll: "બધું રદ કરો",
    manageConsent: "સંમતિ મેનેજ કરો",
    withdrawConsent: "સંમતિ પાછી ખેંચો",
    privacyPolicy: "ગોપનીયતા નીતિ",
    cookiePolicy: "કુકી નીતિ",
    learnMore: "વધુ જાણો",
    confirm: "પુષ્ટિ કરો",
    cancel: "રદ કરો",
    close: "બંધ કરો",
    language: "ભાષા",
    weUseCookies: "અમે કુકીઝનો ઉપયોગ કરીએ છીએ",
    grievanceOfficer: "ફરિયાદ અધિકારી",
    dataRightsRequest: "ડેટા અધિકાર વિનંતી સબમિટ કરો",
    dataRightsRequestHint: "તમારા ડેટાને ઍક્સેસ, સુધારો અથવા કાઢી નાખો, અથવા ફરિયાદ નોંધાવો",
    alwaysActive: "હંમેશા સક્રિય",
    securedBy: "દ્વારા સુરક્ષિત",
  },
  mr: {
    acceptAll: "सर्व स्वीकारा",
    rejectAll: "सर्व नाकारा",
    preferences: "प्राधान्ये",
    savePreferences: "प्राधान्ये जतन करा",
    revokeAll: "सर्व रद्द करा",
    manageConsent: "संमती व्यवस्थापित करा",
    withdrawConsent: "संमती मागे घ्या",
    privacyPolicy: "गोपनीयता धोरण",
    cookiePolicy: "कुकी धोरण",
    learnMore: "अधिक जाणा",
    confirm: "पुष्टी करा",
    cancel: "रद्द करा",
    close: "बंद करा",
    language: "भाषा",
    weUseCookies: "आम्ही कुकीज वापरतो",
    grievanceOfficer: "तक्रार अधिकारी",
    dataRightsRequest: "डेटा हक्क विनंती सादर करा",
    dataRightsRequestHint: "तुमचा डेटा पहा, दुरुस्त करा किंवा हटवा, किंवा तक्रार नोंदवा",
    alwaysActive: "नेहमी सक्रिय",
    securedBy: "द्वारे सुरक्षित",
  },
  ta: {
    acceptAll: "அனைத்தையும் ஏற்கவும்",
    rejectAll: "அனைத்தையும் நிராகரிக்கவும்",
    preferences: "விருப்பங்கள்",
    savePreferences: "விருப்பங்களை சேமி",
    revokeAll: "அனைத்தையும் திரும்பப்பெறு",
    manageConsent: "ஒப்புதலை நிர்வகி",
    withdrawConsent: "ஒப்புதலை திரும்பப் பெறு",
    privacyPolicy: "தனியுரிமைக் கொள்கை",
    cookiePolicy: "குக்கி கொள்கை",
    learnMore: "மேலும் அறிக",
    confirm: "உறுதிப்படுத்து",
    cancel: "ரத்துசெய்",
    close: "மூடு",
    language: "மொழி",
    weUseCookies: "நாங்கள் குக்கீகளைப் பயன்படுத்துகிறோம்",
    grievanceOfficer: "குறைதீர்ப்பு அதிகாரி",
    dataRightsRequest: "தரவு உரிமை கோரிக்கையை சமர்ப்பிக்கவும்",
    dataRightsRequestHint: "உங்கள் தரவை அணுகவும், திருத்தவும் அல்லது நீக்கவும், அல்லது புகார் பதிவு செய்யவும்",
    alwaysActive: "எப்போதும் செயலில்",
    securedBy: "பாதுகாக்கப்படுகிறது",
  },
  te: {
    acceptAll: "అన్నీ అంగీకరించండి",
    rejectAll: "అన్నీ తిరస్కరించండి",
    preferences: "ఇష్టాలు",
    savePreferences: "ఇష్టాలను సేవ్ చేయి",
    revokeAll: "అన్నీ ఉపసంహరించు",
    manageConsent: "సమ్మతిని నిర్వహించండి",
    withdrawConsent: "సమ్మతిని ఉపసంహరించండి",
    privacyPolicy: "గోప్యతా విధానం",
    cookiePolicy: "కుకీ విధానం",
    learnMore: "మరింత తెలుసుకోండి",
    confirm: "నిర్ధారించండి",
    cancel: "రద్దు చేయి",
    close: "మూసివేయి",
    language: "భాష",
    weUseCookies: "మేము కుకీలను ఉపయోగిస్తాము",
    grievanceOfficer: "ఫిర్యాదు అధికారి",
    dataRightsRequest: "డేటా హక్కుల అభ్యర్థన సమర్పించండి",
    dataRightsRequestHint: "మీ డేటాను యాక్సెస్ చేయండి, సరిచేయండి లేదా తొలగించండి, లేదా ఫిర్యాదు చేయండి",
    alwaysActive: "ఎల్లప్పుడూ సక్రియం",
    securedBy: "ద్వారా సురక్షితం",
  },
  bn: {
    acceptAll: "সব গ্রহণ করুন",
    rejectAll: "সব প্রত্যাখ্যান করুন",
    preferences: "পছন্দসমূহ",
    savePreferences: "পছন্দ সংরক্ষণ করুন",
    revokeAll: "সব প্রত্যাহার করুন",
    manageConsent: "সম্মতি পরিচালনা করুন",
    withdrawConsent: "সম্মতি প্রত্যাহার করুন",
    privacyPolicy: "গোপনীয়তা নীতি",
    cookiePolicy: "কুকি নীতি",
    learnMore: "আরও জানুন",
    confirm: "নিশ্চিত করুন",
    cancel: "বাতিল",
    close: "বন্ধ",
    language: "ভাষা",
    weUseCookies: "আমরা কুকি ব্যবহার করি",
    grievanceOfficer: "অভিযোগ কর্মকর্তা",
    dataRightsRequest: "ডেটা অধিকার অনুরোধ জমা দিন",
    dataRightsRequestHint: "আপনার ডেটা অ্যাক্সেস, সংশোধন বা মুছুন, অথবা অভিযোগ দায়ের করুন",
    alwaysActive: "সর্বদা সক্রিয়",
    securedBy: "দ্বারা সুরক্ষিত",
  },
  kn: {
    acceptAll: "ಎಲ್ಲವನ್ನೂ ಸ್ವೀಕರಿಸಿ",
    rejectAll: "ಎಲ್ಲವನ್ನೂ ನಿರಾಕರಿಸಿ",
    preferences: "ಆದ್ಯತೆಗಳು",
    savePreferences: "ಆದ್ಯತೆಗಳನ್ನು ಉಳಿಸಿ",
    revokeAll: "ಎಲ್ಲವನ್ನೂ ಹಿಂತೆಗೆದುಕೊಳ್ಳಿ",
    manageConsent: "ಸಮ್ಮತಿ ನಿರ್ವಹಿಸಿ",
    withdrawConsent: "ಸಮ್ಮತಿ ಹಿಂತೆಗೆದುಕೊಳ್ಳಿ",
    privacyPolicy: "ಗೌಪ್ಯತಾ ನೀತಿ",
    cookiePolicy: "ಕುಕೀ ನೀತಿ",
    learnMore: "ಇನ್ನಷ್ಟು ತಿಳಿಯಿರಿ",
    confirm: "ದೃಢೀಕರಿಸಿ",
    cancel: "ರದ್ದುಮಾಡಿ",
    close: "ಮುಚ್ಚಿ",
    language: "ಭಾಷೆ",
    weUseCookies: "ನಾವು ಕುಕೀಗಳನ್ನು ಬಳಸುತ್ತೇವೆ",
    grievanceOfficer: "ದೂರು ಅಧಿಕಾರಿ",
    dataRightsRequest: "ಡೇಟಾ ಹಕ್ಕುಗಳ ವಿನಂತಿ ಸಲ್ಲಿಸಿ",
    dataRightsRequestHint: "ನಿಮ್ಮ ಡೇಟಾವನ್ನು ಪ್ರವೇಶಿಸಿ, ಸರಿಪಡಿಸಿ ಅಥವಾ ಅಳಿಸಿ, ಅಥವಾ ದೂರು ಸಲ್ಲಿಸಿ",
    alwaysActive: "ಯಾವಾಗಲೂ ಸಕ್ರಿಯ",
    securedBy: "ಮೂಲಕ ಸುರಕ್ಷಿತ",
  },
  ml: {
    acceptAll: "എല്ലാം അംഗീകരിക്കുക",
    rejectAll: "എല്ലാം നിരസിക്കുക",
    preferences: "മുൻഗണനകൾ",
    savePreferences: "മുൻഗണനകൾ സംരക്ഷിക്കുക",
    revokeAll: "എല്ലാം പിൻവലിക്കുക",
    manageConsent: "സമ്മതം നിയന്ത്രിക്കുക",
    withdrawConsent: "സമ്മതം പിൻവലിക്കുക",
    privacyPolicy: "സ്വകാര്യതാ നയം",
    cookiePolicy: "കുക്കി നയം",
    learnMore: "കൂടുതൽ അറിയുക",
    confirm: "സ്ഥിരീകരിക്കുക",
    cancel: "റദ്ദാക്കുക",
    close: "അടയ്ക്കുക",
    language: "ഭാഷ",
    weUseCookies: "ഞങ്ങൾ കുക്കികൾ ഉപയോഗിക്കുന്നു",
    grievanceOfficer: "പരാതി ഉദ്യോഗസ്ഥൻ",
    dataRightsRequest: "ഡാറ്റ അവകാശ അഭ്യർത്ഥന സമർപ്പിക്കുക",
    dataRightsRequestHint: "നിങ്ങളുടെ ഡാറ്റ ആക്‌സസ് ചെയ്യുക, തിരുത്തുക അല്ലെങ്കിൽ ഇല്ലാതാക്കുക, അല്ലെങ്കിൽ പരാതി നൽകുക",
    alwaysActive: "എല്ലായ്പ്പോഴും സജീവം",
    securedBy: "സുരക്ഷിതമാക്കിയത്",
  },
  pa: {
    acceptAll: "ਸਭ ਸਵੀਕਾਰ ਕਰੋ",
    rejectAll: "ਸਭ ਰੱਦ ਕਰੋ",
    preferences: "ਤਰਜੀਹਾਂ",
    savePreferences: "ਤਰਜੀਹਾਂ ਸੰਭਾਲੋ",
    revokeAll: "ਸਭ ਵਾਪਸ ਲਵੋ",
    manageConsent: "ਸਹਿਮਤੀ ਪ੍ਰਬੰਧਿਤ ਕਰੋ",
    withdrawConsent: "ਸਹਿਮਤੀ ਵਾਪਸ ਲਵੋ",
    privacyPolicy: "ਪਰਾਈਵੇਸੀ ਨੀਤੀ",
    cookiePolicy: "ਕੂਕੀ ਨੀਤੀ",
    learnMore: "ਹੋਰ ਜਾਣੋ",
    confirm: "ਪੁਸ਼ਟੀ ਕਰੋ",
    cancel: "ਰੱਦ ਕਰੋ",
    close: "ਬੰਦ ਕਰੋ",
    language: "ਭਾਸ਼ਾ",
    weUseCookies: "ਅਸੀਂ ਕੂਕੀਜ਼ ਵਰਤਦੇ ਹਾਂ",
    grievanceOfficer: "ਸ਼ਿਕਾਇਤ ਅਧਿਕਾਰੀ",
    dataRightsRequest: "ਡਾਟਾ ਅਧਿਕਾਰ ਬੇਨਤੀ ਜਮ੍ਹਾਂ ਕਰੋ",
    dataRightsRequestHint: "ਆਪਣੇ ਡਾਟੇ ਤੱਕ ਪਹੁੰਚੋ, ਸੁਧਾਰੋ ਜਾਂ ਮਿਟਾਓ, ਜਾਂ ਸ਼ਿਕਾਇਤ ਦਰਜ ਕਰੋ",
    alwaysActive: "ਹਮੇਸ਼ਾ ਸਰਗਰਮ",
    securedBy: "ਦੁਆਰਾ ਸੁਰੱਖਿਅਤ",
  },
  or: {
    acceptAll: "ସବୁ ଗ୍ରହଣ କରନ୍ତୁ",
    rejectAll: "ସବୁ ପ୍ରତ୍ୟାଖ୍ୟାନ କରନ୍ତୁ",
    preferences: "ପସନ୍ଦ",
    savePreferences: "ପସନ୍ଦ ସଂରକ୍ଷଣ କରନ୍ତୁ",
    revokeAll: "ସବୁ ପ୍ରତ୍ୟାହାର କରନ୍ତୁ",
    manageConsent: "ସମ୍ମତି ପରିଚାଳନା କରନ୍ତୁ",
    withdrawConsent: "ସମ୍ମତି ପ୍ରତ୍ୟାହାର କରନ୍ତୁ",
    privacyPolicy: "ଗୋପନୀୟତା ନୀତି",
    cookiePolicy: "କୁକି ନୀତି",
    learnMore: "ଅଧିକ ଜାଣନ୍ତୁ",
    confirm: "ନିଶ୍ଚିତ କରନ୍ତୁ",
    cancel: "ବାତିଲ୍",
    close: "ବନ୍ଦ କରନ୍ତୁ",
    language: "ଭାଷା",
    weUseCookies: "ଆମେ କୁକି ବ୍ୟବହାର କରୁ",
    grievanceOfficer: "ଅଭିଯୋଗ ଅଧିକାରୀ",
    dataRightsRequest: "ଡାଟା ଅଧିକାର ଅନୁରୋଧ ଦାଖଲ କରନ୍ତୁ",
    dataRightsRequestHint: "ଆପଣଙ୍କ ଡାଟା ଆକ୍ସେସ, ସଂଶୋଧନ କିମ୍ବା ବିଲୋପ କରନ୍ତୁ, କିମ୍ବା ଅଭିଯୋଗ ଦାଖଲ କରନ୍ତୁ",
    alwaysActive: "ସର୍ବଦା ସକ୍ରିୟ",
    securedBy: "ଦ୍ୱାରା ସୁରକ୍ଷିତ",
  },
  ur: {
    acceptAll: "سب قبول کریں",
    rejectAll: "سب مسترد کریں",
    preferences: "ترجیحات",
    savePreferences: "ترجیحات محفوظ کریں",
    revokeAll: "سب واپس لیں",
    manageConsent: "رضامندی کا نظم کریں",
    withdrawConsent: "رضامندی واپس لیں",
    privacyPolicy: "رازداری کی پالیسی",
    cookiePolicy: "کوکی پالیسی",
    learnMore: "مزید جانیں",
    confirm: "تصدیق کریں",
    cancel: "منسوخ",
    close: "بند کریں",
    language: "زبان",
    weUseCookies: "ہم کوکیز استعمال کرتے ہیں",
    grievanceOfficer: "شکایت افسر",
    dataRightsRequest: "ڈیٹا کے حقوق کی درخواست جمع کریں",
    dataRightsRequestHint: "اپنے ڈیٹا تک رسائی، درست کریں یا حذف کریں، یا شکایت درج کریں",
    alwaysActive: "ہمیشہ فعال",
    securedBy: "کی طرف سے محفوظ",
  },
  as: {
    acceptAll: "সকলো গ্ৰহণ কৰক",
    rejectAll: "সকলো নাকচ কৰক",
    preferences: "পছন্দসমূহ",
    savePreferences: "পছন্দ সংৰক্ষণ কৰক",
    revokeAll: "সকলো প্ৰত্যাহাৰ কৰক",
    privacyPolicy: "গোপনীয়তা নীতি",
    cookiePolicy: "কুকি নীতি",
    language: "ভাষা",
    grievanceOfficer: "অভিযোগ বিষয়া",
    dataRightsRequest: "ডেটা অধিকাৰ অনুৰোধ দাখিল কৰক",
    dataRightsRequestHint: "আপোনাৰ ডেটা এক্সেছ, সংশোধন বা মচাওক, বা অভিযোগ দাখিল কৰক",
    alwaysActive: "সদায় সক্ৰিয়",
    securedBy: "দ্বাৰা সুৰক্ষিত",
  },
  ne: {
    acceptAll: "सबै स्वीकार गर्नुहोस्",
    rejectAll: "सबै अस्वीकार गर्नुहोस्",
    preferences: "प्राथमिकताहरू",
    savePreferences: "प्राथमिकता बचत गर्नुहोस्",
    revokeAll: "सबै रद्द गर्नुहोस्",
    privacyPolicy: "गोपनीयता नीति",
    cookiePolicy: "कुकी नीति",
    language: "भाषा",
    grievanceOfficer: "गुनासो अधिकारी",
    dataRightsRequest: "डाटा अधिकार अनुरोध पेश गर्नुहोस्",
    dataRightsRequestHint: "आफ्नो डाटा पहुँच, सच्याउनुहोस् वा मेटाउनुहोस्, वा गुनासो दर्ता गर्नुहोस्",
    alwaysActive: "सधैं सक्रिय",
    securedBy: "द्वारा सुरक्षित",
  },
  sa: {
    acceptAll: "सर्वं स्वीकुरुत",
    rejectAll: "सर्वं निराकुरुत",
    preferences: "प्राथान्यानि",
    savePreferences: "प्राथान्यानि रक्षत",
    revokeAll: "सर्वं निवर्तयत",
    privacyPolicy: "गोपनीयतानीतिः",
    cookiePolicy: "कुकीनीतिः",
    language: "भाषा",
    grievanceOfficer: "शिकायताधिकारी",
    dataRightsRequest: "दत्ताधिकारप्रार्थनां समर्पयत",
    dataRightsRequestHint: "स्वदत्तं पश्यत, शोधयत अथवा लोपयत, अथवा शिकायतं दर्शयत",
    alwaysActive: "सदा सक्रियम्",
    securedBy: "द्वारा सुरक्षितम्",
  },
  sd: {
    acceptAll: "سڀ قبول ڪريو",
    rejectAll: "سڀ رد ڪريو",
    preferences: "ترجيحون",
    savePreferences: "ترجيحون محفوظ ڪريو",
    revokeAll: "سڀ واپس وٺو",
    privacyPolicy: "رازداري پاليسي",
    cookiePolicy: "ڪوڪي پاليسي",
    language: "ٻولي",
    grievanceOfficer: "شڪايت آفيسر",
    dataRightsRequest: "ڊيٽا حقن جي درخواست جمع ڪريو",
    dataRightsRequestHint: "پنهنجي ڊيٽا تائين رسائي، درست يا ختم ڪريو، يا شڪايت درج ڪريو",
    alwaysActive: "هميشه فعال",
    securedBy: "طرفان محفوظ",
  },
  doi: {
    acceptAll: "सब स्वीकार करो",
    rejectAll: "सब अस्वीकार करो",
    preferences: "तरजीहां",
    savePreferences: "तरजीहां बचाओ",
    revokeAll: "सब रद्द करो",
    privacyPolicy: "गोपनीयता नीति",
    cookiePolicy: "कुकी नीति",
    language: "भाशा",
    grievanceOfficer: "शिकायत अधिकारी",
    dataRightsRequest: "डेटा अधिकार बिनती जमा करो",
    dataRightsRequestHint: "अपने डेटा तकर पहुंचो, सुधारो जां मटाओ, जां शिकायत दर्ज करो",
    alwaysActive: "हमेशा सक्रिय",
    securedBy: "द्वारा सुरक्षित",
  },
  mai: {
    acceptAll: "सभ स्वीकार करू",
    rejectAll: "सभ अस्वीकार करू",
    preferences: "प्राथमिकतासभ",
    savePreferences: "प्राथमिकतासभ सुरक्षित करू",
    revokeAll: "सभ रद्द करू",
    privacyPolicy: "गोपनीयता नीति",
    cookiePolicy: "कुकी नीति",
    language: "भाषा",
    grievanceOfficer: "शिकायत अधिकारी",
    dataRightsRequest: "डेटा अधिकार अनुरोध जमा करू",
    dataRightsRequestHint: "अपन डेटा पहुँचू, सुधारू वा मेटाउ, वा शिकायत दर्ज करू",
    alwaysActive: "हमेशा सक्रिय",
    securedBy: "द्वारा सुरक्षित",
  },
  brx: {
    acceptAll: "गासैबाव आजाव",
    rejectAll: "गासैबाव नेगासि",
    preferences: "पसन्दफोर",
    savePreferences: "पसन्दफोर थिनाय",
    revokeAll: "गासैबाव बाउखां",
    privacyPolicy: "गुप्त नीति",
    cookiePolicy: "कुकी नीति",
    language: "राव",
    grievanceOfficer: "गोनां आधिकारि",
    dataRightsRequest: "डाटा मोननाय निवेदन हो",
    dataRightsRequestHint: "नोंनि डाटा मोन, सुथाङै एबा खोमोर, एबा गोनां हो",
    alwaysActive: "दैहानाय सक्रिय",
    securedBy: "जोरै सुरक्षित",
  },
  gom: {
    acceptAll: "सगळें मान्य करात",
    rejectAll: "सगळें नाकारात",
    preferences: "प्राधान्यां",
    savePreferences: "प्राधान्यां जतन करात",
    revokeAll: "सगळें रद्द करात",
    privacyPolicy: "गोपनीयता धोरण",
    cookiePolicy: "कुकी धोरण",
    language: "भास",
    grievanceOfficer: "तक्रार अधिकारी",
    dataRightsRequest: "डेटा हक्क विनंती दाखल करात",
    dataRightsRequestHint: "तुमचो डेटा पळयात, सुधारात वा काडात, वा तक्रार नोंदवात",
    alwaysActive: "सदा सक्रिय",
    securedBy: "द्वारा सुरक्षित",
  },
  ks: {
    acceptAll: "سٲری قبوٗل کٔرِو",
    rejectAll: "سٲری رَد کٔرِو",
    preferences: "ترجیحات",
    savePreferences: "ترجیحات مَحفوٗظ کٔرِو",
    revokeAll: "سٲری واپس نِیُو",
    privacyPolicy: "رازدٲری پالِسی",
    cookiePolicy: "کوٗکی پالِسی",
    language: "زبان",
    grievanceOfficer: "شِکایت افسر",
    dataRightsRequest: "ڈیٹا حقوٗق درخواست دِیُو",
    dataRightsRequestHint: "پٞنِس ڈیٹا تام رسٲئی، درست یا حذف کٔرِو، یا شِکایت درج کٔرِو",
    alwaysActive: "ہمیشہ فعال",
    securedBy: "ذٔریعہ محفوظ",
  },
  mni: {
    acceptAll: "ꯂꯣꯏꯅꯕ ꯌꯥꯕꯤꯌꯨ",
    rejectAll: "ꯂꯣꯏꯅꯕ ꯌꯥꯗꯕꯤꯌꯨ",
    preferences: "ꯄꯥꯝꯖꯕꯁꯤꯡ",
    savePreferences: "ꯄꯥꯝꯖꯕꯁꯤꯡ ꯊꯝꯃꯨ",
    revokeAll: "ꯂꯣꯏꯅꯕ ꯍꯟꯊꯍꯜꯂꯨ",
    privacyPolicy: "ꯑꯔꯣꯞꯄ ꯊꯧꯔꯝ",
    cookiePolicy: "ꯀꯨꯀꯤ ꯊꯧꯔꯝ",
    language: "ꯂꯣꯟ",
    grievanceOfficer: "ꯋꯥꯔꯦꯞ ꯑꯣꯐꯤꯁꯔ",
    dataRightsRequest: "ꯗꯦꯇꯥ ꯍꯛ ꯍꯥꯏꯅꯕ ꯄꯤꯌꯨ",
    dataRightsRequestHint: "ꯅꯪꯒꯤ ꯗꯦꯇꯥ ꯑꯦꯛꯁꯦꯁ ꯇꯧ, ꯁꯦꯝꯗꯣꯛꯄ ꯅꯠ꯭ꯔꯒꯥ ꯃꯨꯠꯊꯠꯄ, ꯅꯠ꯭ꯔꯒꯥ ꯋꯥꯔꯦꯞ ꯄꯤꯕ",
    alwaysActive: "ꯃꯇꯝꯗꯨꯝ ꯑꯦꯛꯇꯤꯕ",
    securedBy: "ꯅꯍꯥꯛꯅ ꯉꯛꯄ",
  },
  sat: {
    acceptAll: "ᱡᱷᱚᱛᱚ ᱵᱟᱛᱟᱣ ᱢᱮ",
    rejectAll: "ᱡᱷᱚᱛᱚ ᱵᱟᱹᱛᱤᱞ ᱢᱮ",
    preferences: "ᱠᱩᱥᱤᱭᱟᱜ ᱠᱚ",
    savePreferences: "ᱠᱩᱥᱤᱭᱟᱜ ᱫᱚᱦᱚ ᱢᱮ",
    revokeAll: "ᱡᱷᱚᱛᱚ ᱨᱩᱣᱟᱹᱲ ᱢᱮ",
    privacyPolicy: "ᱩᱠᱩ ᱱᱤᱛᱤ",
    cookiePolicy: "ᱠᱩᱠᱤ ᱱᱤᱛᱤ",
    language: "ᱯᱟᱹᱨᱥᱤ",
    grievanceOfficer: "ᱞᱟᱹᱭ ᱚᱯᱷᱤᱥᱚᱨ",
    dataRightsRequest: "ᱰᱮᱴᱟ ᱦᱚᱠ ᱱᱮᱦᱚᱨ ᱡᱚᱢᱟ ᱢᱮ",
    dataRightsRequestHint: "ᱟᱢᱟᱜ ᱰᱮᱴᱟ ᱧᱮᱞ, ᱥᱩᱫᱷᱨᱟᱹᱣ ᱥᱮ ᱢᱮᱴᱟᱣ ᱢᱮ, ᱥᱮ ᱞᱟᱹᱭ ᱚᱞ ᱢᱮ",
    alwaysActive: "ᱡᱟᱹᱛᱤᱭᱟᱹᱨ ᱠᱟᱹᱢᱤᱭᱟᱹ",
    securedBy: "ᱛᱮ ᱨᱩᱠᱷᱤᱭᱟᱹ",
  },
};

export function getConsentUiString(lang: string | undefined | null, key: ConsentUiStringKey): string {
  const code = (lang || "en").toLowerCase().split(/[-_]/)[0] ?? "en";
  return BY_LANG[code]?.[key] ?? EN[key];
}

/** Native / display labels for language selector (not IndicTrans2). */
const LANGUAGE_NATIVE: Record<string, string> = {
  en: "English",
  as: "অসমীয়া",
  bn: "বাংলা",
  brx: "बड़ो",
  doi: "डोगरी",
  gu: "ગુજરાતી",
  hi: "हिन्दी",
  kn: "ಕನ್ನಡ",
  ks: "کٲشُر",
  gom: "कोंकणी",
  mai: "मैथिली",
  ml: "മലയാളം",
  mni: "ꯃꯤꯇꯩꯂꯣꯟ",
  mr: "मराठी",
  ne: "नेपाली",
  or: "ଓଡ଼ିଆ",
  pa: "ਪੰਜਾਬੀ",
  sa: "संस्कृतम्",
  sat: "ᱥᱟᱱᱛᱟᱲᱤ",
  sd: "سنڌي",
  ta: "தமிழ்",
  te: "తెలుగు",
  ur: "اردو",
};

export function getLanguageDisplayName(code: string | undefined | null): string {
  const base = (code || "en").toLowerCase().split(/[-_]/)[0] ?? "en";
  return LANGUAGE_NATIVE[base] ?? (code || "en").toUpperCase();
}

export function resolveSdkLanguage(
  preferred: string | undefined | null,
  available: string[],
  defaultLanguage = "en",
): string {
  const published = available.length ? available : [defaultLanguage];
  const raw = (preferred || "").trim().toLowerCase();
  if (raw) {
    const base = raw.split(/[-_]/)[0] ?? raw;
    if (published.includes(raw)) return raw;
    if (published.includes(base)) return base;
  }
  if (typeof navigator !== "undefined" && navigator.language) {
    const nav = navigator.language.toLowerCase();
    const navBase = nav.split(/[-_]/)[0] ?? nav;
    if (published.includes(nav)) return nav;
    if (published.includes(navBase)) return navBase;
  }
  if (published.includes(defaultLanguage)) return defaultLanguage;
  return published[0] ?? "en";
}
